import React from 'react'
import { Tooltip } from '@radix-ui/themes'
import cn from 'clsx'
import { MdFolder } from '@react-icons/all-files/md/MdFolder'
import { useSelector } from 'hooks/use-redux'
import { Link } from 'react-router-dom'
import Icon from 'components/ui/icon'
import ExpandToggle from './expand'
export const listLink = {
  SIDEBAR_INBOX_LINK: {
    url: '/',
    regexp: '/',
  },
  SIDEBAR_BROWSE_LINK: {
    url: '/',
    regexp: '/',
  },
  SIDEBAR_EMAIL: {
    url: '/campaigns/list',
    regexp: '/campaigns',
  },
  SIDEBAR_LISTS: {
    url: '/lists',
    regexp: '/lists',
  },
  SIDEBAR_CONTACTS: {
    url: '/contacts',
    regexp: '/contacts',
  },
  SIDEBAR_LIBRARIES: {
    url: '/catalogs',
    regexp: '/catalogs',
  },
  SIDEBAR_DISCOVER_MUSIC: {
    url: '/discover',
    regexp: '/discover',
  },
  SIDEBAR_WATERMARKING: {
    url: '/watermarking',
    regexp: '/watermarking',
  },
  SIDEBAR_OPPORTUNITIES: {
    url: '/artist-opportunities',
    regexp: '/artist-opportunities',
  },
  SIDEBAR_INSIGHTS: {
    url: '/insights',
    regexp: '/insights',
  },
  SIDEBAR_SEARCH_PAGE: {
    url: '/search',
    regexp: '/search',
  },
  SIDEBAR_BRIEFS: {
    url: '/briefs',
    regexp: '/briefs',
  },
}
type BaseProps = {
  id: string
  selected?: boolean
  expanded?: boolean
  compact?: boolean
  onClick?: (...args: Array<any>) => any
  nodeRight?: React.ReactNode
}

type Props = BaseProps &
  (
    | { name: string; tooltipLabel?: string }
    | { name: React.ReactNode; tooltipLabel: string }
  )
export default function SidebarSection(
  props: Props,
): React.ReactNode | React.ReactElement<'div'> {
  const location = useSelector(
    state => state.routing?.locationBeforeTransitions || window.location,
  )
  let icon
  let linkSettings
  let expandable = false
  const selected = props.selected || null
  const id = props.id
  const compact = props.compact

  switch (id) {
    case 'CUSTOM_CHANNEL':
      icon = <MdFolder />
      expandable = true
      break

    case 'SIDEBAR_INBOX':
      icon = <Icon type="inbox" />
      expandable = true
      break

    case 'SIDEBAR_INBOX_LINK':
      icon = <Icon type="inbox" />
      linkSettings = listLink.SIDEBAR_INBOX_LINK
      break

    case 'SIDEBAR_BROWSE_LINK':
      icon = <Icon type="browse" />
      linkSettings = listLink.SIDEBAR_BROWSE_LINK
      break

    case 'SIDEBAR_EMAIL':
      icon = <Icon type="mail-send" />
      linkSettings = listLink.SIDEBAR_EMAIL
      break

    case 'SIDEBAR_LISTS':
      icon = <Icon type="format-list" />
      linkSettings = listLink.SIDEBAR_LISTS
      break

    case 'SIDEBAR_CONTACTS':
      icon = <Icon type="user-circle" />
      linkSettings = listLink.SIDEBAR_CONTACTS
      break

    case 'SIDEBAR_LIBRARIES':
      icon = <Icon type="libraries" />
      linkSettings = listLink.SIDEBAR_LIBRARIES
      break

    case 'SIDEBAR_DISCOVER_MUSIC':
      icon = <Icon type="libraries" />
      linkSettings = listLink.SIDEBAR_DISCOVER_MUSIC
      break

    case 'SIDEBAR_WATERMARKING':
      icon = <Icon type="watermarking" />
      linkSettings = listLink.SIDEBAR_WATERMARKING
      break

    case 'SIDEBAR_OPPORTUNITIES':
      icon = <Icon type="crisis-alert" />
      linkSettings = listLink.SIDEBAR_OPPORTUNITIES
      break

    case 'SIDEBAR_INSIGHTS':
      icon = <Icon type="stats" />
      linkSettings = listLink.SIDEBAR_INSIGHTS
      break

    case 'SIDEBAR_SEARCH_PAGE':
      icon = <Icon type="search" />
      linkSettings = listLink.SIDEBAR_SEARCH_PAGE
      break

    case 'SIDEBAR_BRIEFS':
      icon = <Icon type="edit-document" />
      linkSettings = listLink.SIDEBAR_BRIEFS
      break
  }

  let item = (
    <>
      <div className="Sidebar_ItemIcon">{icon}</div>
      <div className="Sidebar_Label">{props.name}</div>
      {props.nodeRight}
    </>
  )

  let el: React.ReactElement
  if (linkSettings) {
    el = (
      <Link
        className={cn('Sidebar_BaseItem Sidebar_Section', {
          'Sidebar_Section--Selected': location.pathname.match(
            linkSettings.regexp,
          ),
        })}
        onClick={props.onClick}
        to={linkSettings.url}
      >
        {item}
      </Link>
    )
  } else {
    el = (
      <div
        className="Sidebar_BaseItem Sidebar_Section"
        data-sel={selected}
        onClick={props.onClick}
      >
        {item}
        {expandable && (
          <div className="Sidebar_ToRight">
            <ExpandToggle expanded={props.expanded} />
          </div>
        )}
      </div>
    )
  }

  return compact ? (
    <Tooltip
      className="Sidebar_NavTooltip"
      content={props.tooltipLabel ?? props.name}
      side="right"
    >
      {el}
    </Tooltip>
  ) : (
    el
  )
}
